/*
 * shopPickerReducer
 * Description
 * The reducer takes care of our data. Using actions, we can
 * update our application state. To add a new action,
 * add it to the switch statement in the reducer function
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 05-May-2025
 */

import { produce } from 'immer'
import { actionTypes } from '../actions/shopPicker'

// Funzione di utilità per derivare lo stato dal payload
const computeSelectionStatus = (selectorGroups, shopSelections, selectedShopInfo = null) => {
  let totalAvailableShops = 0
  let totalSelectedShops = 0
  let activeGroup = null

  for (const groupKey of Object.keys(shopSelections)) {
    if (groupKey === 'status') continue // 🛑 Skip chiave speciale

    const entries = shopSelections[groupKey]
    if (!Array.isArray(entries) || entries.length === 0) continue

    if (!activeGroup) activeGroup = groupKey

    const groupData = selectorGroups.find(g => g.group === groupKey)
    const shopsList = groupData?.shopsList || []

    totalAvailableShops += shopsList.reduce((acc, parent) => {
      const hasChildren = Array.isArray(parent.shops) && parent.shops.length > 0
      return acc + (hasChildren ? parent.shops.length : 1)
    }, 0)

    totalSelectedShops += entries.reduce((acc, entry) => {
      const parentSelected = entry.parent?.isSelected === true
      const selectedShops = entry.shops?.filter(shop => shop.isSelected) || []
      const allShopCount = entry.allShopCount ?? selectedShops.length
      const isFullParentSelection = parentSelected && selectedShops.length === allShopCount

      if (isFullParentSelection || (!entry.shops || entry.shops.length === 0)) {
        return acc + 1
      }

      return acc + selectedShops.length
    }, 0)
  }

  // ✅ Fallback da profiling se necessario
  if (totalAvailableShops === 0 && selectedShopInfo) {
    totalAvailableShops = selectedShopInfo.numberOfShops || 0
    totalSelectedShops = selectedShopInfo.numberOfSelectedShops || 0
    activeGroup = selectedShopInfo.group || activeGroup
  }

  const hasAnySelectedShops = totalSelectedShops > 0
  const areAllSelected = totalSelectedShops === totalAvailableShops && totalAvailableShops > 0
  const areSomeOfThemSelected = hasAnySelectedShops && totalSelectedShops < totalAvailableShops

  return {
    selectionStatus: {
      activeGroup,
      hasAnySelectedShops,
      totalSelectedShops,
      totalAvailableShops,
      areAllSelected,
      areSomeOfThemSelected,
      shouldShowDashboard: hasAnySelectedShops,
      shouldShowEmptyStateCard: !hasAnySelectedShops,
      shouldShowPartialSelectionToast: areSomeOfThemSelected
    }
  }
}

function mergeShops (existingShops = [], newShops = []) {
  const shopMap = new Map(
    existingShops.map(shop => [shop.code, shop])
  )

  newShops.forEach(shop => {
    shopMap.set(shop.code, shop)
  })

  return Array.from(shopMap.values())
}

const ACTION_HANDLERS = {
  [actionTypes.DO_SET_SHOP_SELECTION]: produce((draft, action) => {
    const { group, data, selectorGroups, selectedShopInfo } = action

    if (!draft[group]) {
      draft[group] = []
    }

    if (Array.isArray(data)) {
      draft[group] = data
    } else {
      const parentIndex = draft[group].findIndex(
        (entry) => entry.parent.code === data.parent.code
      )

      if (data.shops.length > 0 || data.parent.isSelected) {
        if (parentIndex > -1) {
          draft[group][parentIndex] = data
        } else {
          draft[group].push(data)
        }
      } else if (parentIndex > -1) {
        draft[group].splice(parentIndex, 1)
      }
    }

    draft.status = computeSelectionStatus(selectorGroups || [], draft, selectedShopInfo).selectionStatus
    draft.shopSelectionChanged = true
  }),

  [actionTypes.CLEAR_SHOP_SELECTION]: produce((draft, action) => {
    const group = action.group
    draft[group] = []
    draft.status = {}
  }),

  [actionTypes.CLEAR_COSTS_SHOP_SELECTION]: produce((draft, action) => {
    const group = action.group
    // Per i costi, pulisci sia il gruppo normale che quello costs-specific
    draft[group] = []
    if (draft.costsShopLocalSelection && draft.costsShopLocalSelection[group]) {
      draft.costsShopLocalSelection[group] = []
    }
    draft.status = {}
  }),

  [actionTypes.DO_SET_SHOP_SEARCH_TERM]: produce((draft, action) => {
    draft.searchTerm = action.payload
  }),

  [actionTypes.DO_CLEAR_SHOP_SEARCH_TERM]: produce((draft) => {
    draft.searchTerm = ''
  }),

  [actionTypes.SET_SHOP_SELECTION_SUCCESS]: produce((draft, action) => {
    draft.shopSelectionStatus = action.response
  }),

  [actionTypes.SET_COSTS_SHOP_SELECTION_SUCCESS]: produce((draft, action) => {
    draft.costsShopSelectionStatus = action.response
    const { group, data } = action.payload || {}
    if (group && Array.isArray(data)) {
      // Keep the confirmed snapshot independent from editable picker state.
      draft.costsShopConfirmedSelection = { [group]: JSON.parse(JSON.stringify(data)) }
      draft.costsShopLocalSelection = { [group]: data }
    }
    draft.costShopSelected = true
    draft.costShopApplied = false
    draft.hideEmptyState = false
  }),
  [actionTypes.GET_SHOP_LIST_GT_SUCCESS]: produce((draft, action) => {
    const response = action.response || {}
    // Replace the entire list with new page data (no concatenation)
    draft.gtShopList = response.shops || []
    draft.gtShopListPagination = {
      total: response.total || 0,
      pageNumber: response.pageNumber || 0,
      pageSize: response.pageSize || 15
    }
    draft.gtShopListLoading = false
  }),
  [actionTypes.GET_SHOP_LIST_GT_REQUEST]: produce((draft, action) => {
    draft.gtShopListLoading = true
  }),
  [actionTypes.GET_SHOP_LIST_GT_FAILURE]: produce((draft, action) => {
    draft.gtShopListLoading = false
  }),

  [actionTypes.DO_SET_SHOP_LOCAL_SELECTION]: produce((draft, action) => {
    const { group, data } = action.payload

    if (!Array.isArray(data)) return

    if (!draft.shopLocalSelection[group]) {
      draft.shopLocalSelection[group] = []
    }

    const existing = draft.shopLocalSelection[group]

    // Mappa esistenti per parent.code
    const parentMap = new Map(
      existing.map(entry => [entry.parent.code, entry])
    )

    data.forEach(entry => {
      const parentCode = entry.parent.code
      const existingEntry = parentMap.get(parentCode)

      if (existingEntry) {
        // Merge shops selection
        const mergedShops = mergeShops(existingEntry.shops, entry.shops)
        parentMap.set(parentCode, {
          parent: entry.parent,
          shops: mergedShops
        })
      } else {
        parentMap.set(parentCode, entry)
      }
    })

    // Salva il risultato
    draft.shopLocalSelection[group] = Array.from(parentMap.values())
  }),

  [actionTypes.DO_SET_COSTS_SHOP_LOCAL_SELECTION]: produce((draft, action) => {
    const { group, data } = action.payload

    if (!Array.isArray(data)) return

    // Per i Costs: usa una location separata per evitare interferenze
    if (!draft.costsShopLocalSelection) {
      draft.costsShopLocalSelection = {}
    }

    // Sostituisci completamente invece di fare merge
    // perché i dati sono già filtrati correttamente
    draft.costsShopLocalSelection[group] = data
  }),

  [actionTypes.DO_SET_SHOP_SELECTION_MODE]: produce((draft, action) => {
    draft.shopSelectionMode = action.mode || 'checkbox'
    draft.shopSelectionFromPage = action.page || 'default'
  }),

  [actionTypes.DO_FORCE_SHOW_COSTS_SHOP_PICKER_POPUP]: produce((draft, action) => {
    draft.forceShowCostsShopPickerPopup = true
  }),

  [actionTypes.DO_RESET_FORCE_SHOW_COSTS_SHOP_PICKER_POPUP]: produce((draft, action) => {
    draft.forceShowCostsShopPickerPopup = false
  }),

  [actionTypes.DO_CLEAR_SHOP_LOCAL_SELECTION]: produce((draft, action) => {
    draft.shopLocalSelection = {}
  }),

  [actionTypes.DO_CLEAR_COSTS_SHOP_LOCAL_SELECTION]: produce((draft, action) => {
    const group = typeof action.payload === 'string' ? action.payload : action.payload?.group

    if (!draft.costsShopLocalSelection) {
      draft.costsShopLocalSelection = {}
      return
    }

    if (group) {
      // Cancella solo il gruppo specifico dei Costs
      delete draft.costsShopLocalSelection[group]
    } else {
      // Se non specificato, cancella tutto per i Costs
      draft.costsShopLocalSelection = {}
    }
  }),
  [actionTypes.DO_SET_LOCAL_SELECTED_TAB]: produce((draft, action) => {
    draft.localSelectedTab = action.payload
  }),
  [actionTypes.DO_SET_COSTS_SHOP_SELECTION]: produce((draft, action) => {
    draft.hideEmptyState = true
  }),
  [actionTypes.SET_COSTS_SHOP_SELECTION_REQUEST]: produce((draft, action) => {
    draft.costShopSelected = false
    draft.costShopApplied = true
    draft.hideEmptyState = true
  }),
  [actionTypes.SET_COSTS_SHOP_SELECTION_FAILURE]: produce((draft, action) => {
    draft.costShopSelected = true
    draft.costShopApplied = false
    draft.hideEmptyState = false
  }),
  [actionTypes.DO_RESET_SHOP_SELECTION_CHANGED]: produce((draft) => {
    draft.shopSelectionChanged = false
  }),
  [actionTypes.SET_DISPUTE_SHOP_SELECTION_SUCCESS]: produce((draft, action) => {
    draft.disputeShopSelectionStatus = action.response
  }),
  [actionTypes.DO_FORCE_SHOW_DISPUTE_SHOP_PICKER_POPUP]: produce((draft) => {
    draft.forceShowDisputeShopPickerPopup = true
  }),
  [actionTypes.DO_RESET_FORCE_SHOW_DISPUTE_SHOP_PICKER_POPUP]: produce((draft) => {
    draft.forceShowDisputeShopPickerPopup = false
  })
  // @generator reducer:type:action
}

const initialState = {
  searchTerm: '',
  status: {},
  shopSelectionStatus: {},
  costsShopSelectionStatus: {},
  costsShopConfirmedSelection: {},
  shopLocalSelection: {},
  costsShopLocalSelection: {}, // Separato per i Costs
  gtShopList: [],
  gtShopListPagination: {
    total: 0,
    pageNumber: 0,
    pageSize: 15
  },
  gtShopListLoading: false,
  shopSelectionMode: 'checkbox',
  shopSelectionFromPage: 'default',
  forceShowCostsShopPickerPopup: false,
  localSelectedTab: null,
  costShopSelected: false,
  disputeShopSelectionStatus: {},
  forceShowDisputeShopPickerPopup: false
}

const shopPicker = (state = initialState, action) => {
  const handler = ACTION_HANDLERS[action.type]
  return handler ? handler(state, action) : state
}

export default shopPicker
